import React from "react";
import { LinearProgressBar } from "@vibe/core";
import { MdLightbulb } from "react-icons/md";
import { Info, Check } from "@vibe/icons";
import { IoSync } from "react-icons/io5";

interface AddItemProgressBannerProps {
  progress: number;
  isCompleted: boolean;
  hasFailed?: boolean;
  /** Where the item was just shared to. Without it the banner keeps its generic message. */
  sharedBoardName?: string;
  /** Columns now syncing with that board. Omitted until the item view has reloaded. */
  syncingColumnCount?: number;
  onReviewColumns?: () => void;
  /** Tight variant for small surfaces (the column dialog): less padding, no Pro Tip. */
  compact?: boolean;
}

const getStateStyle = (isCompleted: boolean, hasFailed: boolean) => {
  if (hasFailed) {
    return {
      accent: "border-l-[#D83A52]",
      iconBg: "bg-[#D83A52]",
      icon: <Info size={16} color="#fff" />,
    };
  }
  if (isCompleted) {
    return {
      accent: "border-l-[#258750]",
      iconBg: "bg-[#258750]",
      icon: <Check size={16} color="#fff" />,
    };
  }
  return {
    accent: "border-l-[#0073EA]",
    iconBg: "bg-[#0073EA]",
    icon: <IoSync size={16} color="#fff" className="animate-spin-slow" />,
  };
};

const AddItemProgressBanner = ({
  progress,
  isCompleted,
  hasFailed = false,
  sharedBoardName,
  syncingColumnCount,
  onReviewColumns,
  compact = false,
}: AddItemProgressBannerProps) => {
  const statusMessage = hasFailed
    ? "Failed to add item. Please try again."
    : isCompleted
      ? sharedBoardName
        ? `Shared to ${sharedBoardName}`
        : "Item added successfully!"
      : "Adding your item to the board\u2026";

  const barColor = hasFailed ? ("negative" as const) : undefined;
  const { accent, iconBg, icon } = getStateStyle(isCompleted, hasFailed);

  return (
    <div
      role="status"
      className={`${compact ? "mb-3 p-4" : "mb-5 p-10"} overflow-hidden rounded-lg border border-[#D0D4E4] border-l-4 ${accent} bg-[#F6F7FB] transition-colors duration-300`}
    >
      <div className="flex items-center gap-3 !mb-3">
        <div
          aria-hidden="true"
          className={`flex shrink-0 items-center justify-center rounded-full p-1.5 ${iconBg} transition-colors duration-300`}
        >
          {icon}
        </div>
        <p className="font-semibold text-[#323338]">
          {statusMessage}
          {isCompleted && !hasFailed && syncingColumnCount !== undefined && (
            <span className="font-normal text-[#676879]">
              {" · "}
              {syncingColumnCount} column{syncingColumnCount !== 1 ? "s" : ""} syncing
            </span>
          )}
        </p>
        {isCompleted && !hasFailed && onReviewColumns && (
          <button
            type="button"
            onClick={onReviewColumns}
            className="p-0 ml-auto text-sm font-medium bg-transparent border-0 cursor-pointer text-[#0073EA] hover:underline"
          >
            Review columns
          </button>
        )}
      </div>

      <LinearProgressBar
        value={Math.round(progress)}
        size="medium"
        indicateProgress
        barStyle={barColor}
      />

      {!isCompleted && !compact && (
        <div className="mt-6 rounded-md bg-[#EDF0F7] p-5">
          <div className="flex gap-2 items-center mb-4">
            <div className="bg-[#ffcb00] flex items-center justify-center rounded-full p-1">
              <MdLightbulb size={16} color="#fff" />
            </div>
            <h3 className="font-semibold tracking-wider uppercase text-[#0373e9]">Pro Tip</h3>
          </div>
          <p className="text-sm leading-5 text-[#676879] text-pretty">
            Go to <span className="font-semibold text-[#323338]">integrations</span> → search{" "}
            <span className="font-semibold text-[#323338]">Same Item Multiple Boards</span> → set
            automations to{" "}
            <span className="font-semibold text-[#0373e9]">10x your productivity!</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default AddItemProgressBanner;
